/**
 * `DomainStep` — onboarding step 0 (Add site). On submit, calls
 * `POST /v1/sites` and advances to step 1 (Install + skills) via
 * `onCreated`. The KV-decoupled create from commit `5a9fe97` means a
 * Cloudflare KV outage no longer blocks this step. Per ADR-0043 the
 * site is created at `pending_proof`; ownership is proven later by the
 * install snippet's proof token (no DNS record), surfaced in
 * `ProofStatusPanel` at step 2 — NOT a DNS/CNAME step.
 */

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useApi } from '../../api/api-context.js';
import { ApiError, type PlanLimitInfo } from '../../api/http-client.js';
import { useSites, useToast } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import { isClaimEntry, sanitizePrefillDomainParam } from '../../lib/prefill-domain.js';
import { useC } from '../../lib/theme.js';
import { NumLabel, PlanLimitNotice } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

// Mirrors the server-side DOMAIN_RE in `services/api/src/http/routes/v1/sites.ts`.
export const DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

export function deriveName(domain: string): string {
  // Strip a leading "www." first — otherwise "www.example.com" derives to
  // "Www" instead of "Example" (the label the merchant actually cares about).
  const registrable = domain.replace(/^www\./i, '');
  const head = registrable.split('.')[0] ?? registrable;
  return (
    head.replace(/[^a-z0-9]/gi, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    || domain
  );
}

/**
 * Domain carry-over (2026-07-08, widened to existing accounts
 * 2026-07-09) — one-shot read of the `?prefill_domain=` param the
 * verify redirect appends whenever a pending domain exists (the domain
 * the visitor typed in the marketing hero flow), regardless of whether
 * the account was just created or already existed.
 * Consumed at mount: validated against the same DOMAIN_RE as manual
 * input (an invalid value prefills nothing), then stripped from the
 * URL via replaceState so a refresh doesn't re-apply it after the
 * merchant edits the field. Pure; exported for unit tests.
 */
export function consumePrefillDomain(): string {
  const domain = peekPrefillDomain();
  stripPrefillDomainParam();
  return domain;
}

/**
 * ADR-0186 S1 — read + validate the prefill WITHOUT consuming it. The
 * onboarding shell's auto-create peeks first and only strips the param
 * once the site is actually created, so a failed auto-create leaves the
 * prefill intact for the one-click DomainStep fallback.
 */
export function peekPrefillDomain(): string {
  if (typeof window === 'undefined') return '';
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get('prefill_domain');
    // INTERSECTION rule (qa #570 rounds 2+3): the value must pass BOTH
    // grammars — the wire sanitizer (hostname shape: no leading/
    // trailing/double dots, no edge hyphens; `DOMAIN_RE` alone admits
    // all of those) AND the site-create DOMAIN_RE (alphabetic TLD; the
    // wire grammar alone admits numeric TLDs). The carry chain's finish
    // line is POST /v1/sites, so anything failing either grammar drops
    // to the ordinary editable form here — recoverable — instead of
    // feeding an auto-create the server may reject and stranding the
    // "Creating your site." interstitial with no escape.
    const t = sanitizePrefillDomainParam(raw);
    if (t === null) return '';
    return DOMAIN_RE.test(t) ? t : '';
  } catch {
    return '';
  }
}

/** Strip `?prefill_domain=` from the URL (one-shot consumption). */
export function stripPrefillDomainParam(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('prefill_domain')) return;
    url.searchParams.delete('prefill_domain');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    /* non-fatal */
  }
}

export interface DomainStepProps {
  onCreated: () => void;
  /**
   * Optional so the existing direct-render tests (`<DomainStep
   * onCreated={...} />`, no router in scope) keep type-checking. When
   * absent, the plan-limit notice below still renders the server's
   * friendly message — it just omits the "View plans" button.
   */
  setView?: (v: View) => void;
}

export function DomainStep({ onCreated, setView }: DomainStepProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const toast = useToast();
  const { addSite, setActiveSite } = useSites();
  const [domain, setDomainInput] = useState(() => consumePrefillDomain());
  const [pending, setPending] = useState(false);
  // Whether the field arrived PREFILLED from the marketing carry-over
  // (captured once, before any keystroke). A cold "add a site" starts
  // empty and this stays false.
  // Claim entries (report page → /sign-in?prefill_domain, ADR-0201
  // amendment) get the VISIBLE OFFER: the prefilled editable form with
  // an explicit submit — never the auto-submit or the "Creating your
  // site." interstitial. A share link carries no email round-trip, so
  // the click on THIS form is the proof of intent (qa #570 round 2).
  const cameFromPrefillRef = useRef(domain !== '' && !isClaimEntry());
  // ADR-0143 §8 — a 402 from the entitlements cap-check gets its own
  // persistent inline notice (not a 4s-dismiss toast) with the
  // server's friendly message + an upgrade CTA, instead of the raw
  // `server_error:402 — site_limit_reached` a generic toast produced.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);
  // On the prefill carry-over path we render the "Setting up {domain}"
  // interstitial (not the form) while the one-shot auto-submit runs, so
  // the merchant never sees a form flash for a domain they already typed.
  // We only fall through to the editable form if that auto-submit FAILS —
  // then there is a real decision to make and the form is correct.
  const [autoFailed, setAutoFailed] = useState(false);

  const valid = DOMAIN_RE.test(domain);

  const submit = async (): Promise<void> => {
    if (!valid || pending) return;
    setPending(true);
    setPlanLimit(null);
    try {
      const created = await api.sites.create({ domain, name: deriveName(domain) });
      addSite(created);
      setActiveSite(created);
      onCreated();
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'plan_limit_reached' && err.plan_limit !== undefined) {
        setPlanLimit(err.plan_limit);
        setAutoFailed(true);
        return;
      }
      if (err instanceof ApiError && err.kind === 'conflict') {
        // Re-add: the site already exists for this account. Pull it
        // from the list and resume against it. Case-insensitive match —
        // a legacy site stored mixed-case (e.g. "Aa.com") must resolve
        // against the lowercased input so the merchant resumes the
        // existing site instead of seeing a dead-end error.
        const list = await api.sites.list();
        const needle = domain.toLowerCase();
        const existing = list.find((s) => s.domain.toLowerCase() === needle);
        if (existing !== undefined) {
          // ADR-0050 §4 — surface the resume as a positive lime-toned
          // toast BEFORE advancing. The previous failure-path coral
          // toast no longer fires on this branch (the conflict is
          // structurally a "you already have this site" — not an
          // error).
          toast({
            title: `${existing.domain} is already on Sill`,
            body: 'Resuming your existing setup.',
            tone: 'lime',
          });
          addSite(existing);
          setActiveSite(existing);
          onCreated();
          return;
        }
      }
      toast({
        title: 'Could not add site',
        body: err instanceof Error ? err.message : 'Unknown error',
        tone: 'coral',
      });
      setAutoFailed(true);
    } finally {
      setPending(false);
    }
  };

  // ADR-0186 declutter (founder 2026-07-14: "we are double onboarding the
  // domain… the step before can trigger the continue on this step"). When
  // the domain arrived prefilled from the marketing hero carry-over, the
  // merchant already entered it — auto-submit ONCE so this step self-
  // completes (create, or resume-on-conflict) and advances to Skills
  // instead of asking for the same domain again. A cold add-a-site starts
  // empty, so this never fires there. Only the shell-level auto-create's
  // FALLBACK reaches here with a prefill; this closes that last gap.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (autoSubmittedRef.current) return;
    if (!cameFromPrefillRef.current) return;
    if (!DOMAIN_RE.test(domain) || pending) return;
    autoSubmittedRef.current = true;
    void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill carry-over: show the same "Setting up {domain}" moment the
  // shell uses, not the domain form, until (and unless) the auto-submit
  // fails. Kills the ~1-frame form flicker the founder saw (2026-07-15).
  if (cameFromPrefillRef.current && !autoFailed) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="py-16 text-center"
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 12,
          }}
        >
          SETTING UP
        </div>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(32px, 6vw, 48px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
            marginBottom: 16,
          }}
        >
          {domain}
        </h1>
        <p style={{ fontFamily: sansFont, fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
          Creating your site. One moment.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <NumLabel n={1}>Step 1 · Domain</NumLabel>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(40px, 7vw, 64px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -2,
          marginBottom: 28,
        }}
      >
        What domain should
        <br />
        Sill <em style={{ color: C.limeFg }}>govern</em>?
      </h1>
      <div
        className="flex flex-col sm:flex-row items-stretch overflow-hidden mb-4"
        style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4 }}
      >
        <div
          className="flex items-center px-5 py-3 sm:py-0 sm:border-r"
          style={{
            borderColor: C.border,
            fontFamily: monoFont,
            fontSize: 13,
            color: C.muted,
          }}
        >
          https://
        </div>
        <input
          value={domain}
          onChange={(e) =>
            setDomainInput(
              e.target.value.replace(/^https?:\/\//i, '').replace(/\/$/, ''),
            )
          }
          autoFocus
          className="flex-1 px-4 py-4 sm:py-5 bg-transparent outline-none"
          style={{ fontFamily: monoFont, fontSize: 15, color: C.text }}
          placeholder="yourdomain.com"
          aria-label="Site domain"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && valid && !pending) void submit();
          }}
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!valid || pending}
          className="flex items-center justify-center gap-2 px-6 py-3 sm:py-0"
          style={{
            background: valid ? C.lime : C.surface2,
            color: valid ? C.ink : C.muted,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 14,
            opacity: pending ? 0.6 : valid ? 1 : 0.6,
          }}
        >
          Continue <ArrowRight size={16} strokeWidth={2.5} />
        </button>
      </div>
      {planLimit !== null ? (
        <div className="mb-4">
          <PlanLimitNotice
            planLimit={planLimit}
            {...(setView !== undefined ? { onViewPlans: () => setView('billing') } : {})}
          />
        </div>
      ) : null}
    </motion.div>
  );
}
